import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const KEY = "orbit_splash_at";
const COOLDOWN_MS = 30 * 60 * 1000;

// Broadcast ident sound: low whoosh (filtered noise sweep) + rising three-note chime. Skipped when muted or autoplay blocked.
function playIdent() {
  if (localStorage.getItem("orbit_splash_mute") === "1") return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext; if (!Ctx) return;
    const ctx = new Ctx(); const t0 = ctx.currentTime;
    const master = ctx.createGain(); master.gain.value = 0.6; master.connect(ctx.destination);
    const len = ctx.sampleRate * 1.6, buf = ctx.createBuffer(1, len, ctx.sampleRate), d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = ctx.createBufferSource(); src.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 0.9; bp.frequency.setValueAtTime(180, t0); bp.frequency.exponentialRampToValueAtTime(2400, t0 + 1.3);
    const ng = ctx.createGain(); ng.gain.setValueAtTime(0.0001, t0); ng.gain.exponentialRampToValueAtTime(0.35, t0 + 0.5); ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.6);
    src.connect(bp); bp.connect(ng); ng.connect(master); src.start(t0); src.stop(t0 + 1.6);
    [[523.25, 1.15], [783.99, 1.3], [1046.5, 1.45]].forEach(([f, at]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = "sine"; o.frequency.value = f;
      const o2 = ctx.createOscillator(); o2.type = "triangle"; o2.frequency.value = f * 2; const g2 = ctx.createGain(); g2.gain.value = 0.12;
      o.connect(g); o2.connect(g2); g2.connect(g); g.connect(master);
      const s = t0 + at; g.gain.setValueAtTime(0.0001, s); g.gain.exponentialRampToValueAtTime(0.5, s + 0.03); g.gain.exponentialRampToValueAtTime(0.0001, s + 1.4);
      o.start(s); o2.start(s); o.stop(s + 1.5); o2.stop(s + 1.5);
    });
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    setTimeout(() => ctx.close().catch(() => {}), 3500);
  } catch { /* no audio */ }
}

// Al Jazeera-style ident: black screen, logo fades/zooms in with a light sweep, holds, then fades out
export default function SplashIntro({ children }) {
  const [phase, setPhase] = useState(() => {
    if (window.location.pathname === "/display" || window.location.pathname === "/r" || new URLSearchParams(window.location.search).has("nosplash")) return "done";
    const last = Number(sessionStorage.getItem(KEY) || 0);
    return Date.now() - last < COOLDOWN_MS ? "done" : "in";
  });
  // Hold the heavy app tree until the globe has settled, so React rendering never janks the fly-in
  const [ready, setReady] = useState(phase === "done");
  useEffect(() => { if (ready) return; const t = setTimeout(() => setReady(true), 3200); return () => clearTimeout(t); }, [ready]);
  useEffect(() => { document.body.classList.toggle("splash-on", phase !== "done"); return () => document.body.classList.remove("splash-on"); }, [phase]);
  const [muted, setMuted] = useState(() => localStorage.getItem("orbit_splash_mute") === "1");
  const played = useRef(false);
  const starRef = useRef(null);
  useEffect(() => {
    // Realistic star field: hundreds of stars with varied size/colour + a few distant galaxies, drawn once
    const c = starRef.current; if (!c || phase === "done") return;
    const dpr = Math.min(2, window.devicePixelRatio || 1); const w = c.width = innerWidth * dpr, h = c.height = innerHeight * dpr;
    const ctx = c.getContext("2d"); ctx.clearRect(0, 0, w, h);
    const rnd = (a, b) => a + Math.random() * (b - a);
    for (let i = 0; i < 9; i++) {
      const x = rnd(0, w), y = rnd(0, h), r = rnd(40, 140) * dpr, a = rnd(0, Math.PI);
      ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.scale(1, rnd(.25, .5));
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r); const hue = Math.random() < .5 ? "200,220,255" : "255,200,160";
      g.addColorStop(0, `rgba(${hue},${rnd(.10, .22)})`); g.addColorStop(.4, `rgba(${hue},${rnd(.04, .08)})`); g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.fill(); ctx.restore();
    }
    for (let i = 0; i < 700; i++) {
      const x = rnd(0, w), y = rnd(0, h), r = Math.pow(Math.random(), 3) * 1.8 * dpr + .3, b = rnd(.35, 1);
      const col = Math.random() < .12 ? `rgba(255,214,170,${b})` : Math.random() < .2 ? `rgba(170,200,255,${b})` : `rgba(255,255,255,${b})`;
      ctx.fillStyle = col; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      if (r > 1.4 * dpr) { ctx.fillStyle = `rgba(255,255,255,${b * .25})`; ctx.fillRect(x - r * 4, y - .4 * dpr, r * 8, .8 * dpr); ctx.fillRect(x - .4 * dpr, y - r * 4, .8 * dpr, r * 8); }
    }
  }, [phase]);
  useEffect(() => {
    if (phase === "done") return;
    sessionStorage.setItem(KEY, String(Date.now()));
    const t1 = setTimeout(() => setPhase("out"), 7500);
    const t2 = setTimeout(() => setPhase("done"), 10800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (phase !== "in" || played.current) return;
    // Autoplay policy: play immediately if allowed (PWA / prior interaction), otherwise on first tap
    const go = () => { if (played.current) return; played.current = true; playIdent(); };
    if (navigator.userActivation?.hasBeenActive || window.matchMedia("(display-mode: standalone)").matches) go();
    window.addEventListener("pointerdown", go, { once: true }); window.addEventListener("keydown", go, { once: true });
    return () => { window.removeEventListener("pointerdown", go); window.removeEventListener("keydown", go); };
  }, [phase]);
  if (phase === "done") return children || null;
  const toggleMute = (e) => { e.stopPropagation(); const m = !muted; setMuted(m); localStorage.setItem("orbit_splash_mute", m ? "1" : "0"); };
  return (<>
    {ready && children}
    <div className={`splash-root ${phase === "out" ? "splash-out" : ""}`} data-testid="splash-intro" onClick={() => { setReady(true); setPhase("done"); }} role="presentation">
      <canvas ref={starRef} className="splash-canvas" aria-hidden="true" />
      <div className="splash-nebula" aria-hidden="true" />
      <div className="splash-stars" aria-hidden="true" />
      <div className="splash-glow" aria-hidden="true" />
      <div className="splash-trail" aria-hidden="true" />
      <div className="splash-logo-wrap">
        <img src="/brand/orbit-logo.webp" alt="Orbit PMS" className="splash-logo" draggable={false} />
        <div className="splash-sweep" aria-hidden="true" />
      </div>
      <div className="splash-tag" dir="ltr">Orbit PMS <span className="splash-ar">· مدار</span></div>
      <button type="button" onClick={toggleMute} className="splash-mute" aria-label={muted ? "unmute" : "mute"} data-testid="splash-mute-btn">{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}</button>
    </div>
  </>);
}
